import type { PolicySelection, AudienceSelection } from "@/lib/api/courseworkPolicy";
import { a3Copy } from "@/components/assignments/a3-copy";
import { scaleMaximum, validateScale } from "./scale/model";
import type { GradingScale } from "./scale/types";
import type { QuizDraft } from "../coursework/quiz-author-model";
/**
 * A piece of session work as its editor holds it: the facts a teacher sets, the
 * draft its fields hold, what those fields would send, and the checks a fact
 * passes before it is sent.
 *
 * Due is an instant entered as the course's wall clock. The field shows the
 * stored instant in the course zone, and a typed minute is read back in that
 * zone, never the browser's.
 *
 * A draft's grade item is one of its facts. Once the work is published the
 * grade item is fixed, and every other change is a correction.
 */
import type { ConflictFact } from "@/components/ui/conflict-notice";
import type { AssessmentKind, Assignment, AssignmentStatus, CorrectAssignmentRequest, ExpectedDeliverable, RubricCriterion, SubmissionKind } from "@/lib/api/assignments";
import { EXPECTED_DELIVERABLE_LABEL } from "@/lib/assignment-presentation";
import { classroomDeadline } from "@/lib/classroom-deadline";
import { formatInCourseZone } from "@/lib/session-portal";

/** What the editor reads of an assignment; the session tile's row carries the same facts. */
export type WorkRecord = Pick<
  Assignment,
  | "id"
  | "course_id"
  | "title"
  | "instructions"
  | "expected_deliverable"
  | "points"
  | "due_at"
  | "unit_id"
  | "grade_item_id"
  | "status"
  | "updated_at"
  | "current_version_id"
  | "assessment_kind"
  | "external_url"
  | "rubric"
  | "scale"
  | "policy"
  | "audience"
  | "opens_at"
  | "materials"
>;

/** One shared level while authoring; its share is typed text until publication validates it. */
export interface LevelDraft { id: string; name: string; share: string }

export interface AssessmentDraft {
  kind: AssessmentKind;
  quiz?: QuizDraft;
  externalUrl: string;
  /** A level rubric's criteria add what to look for and optional evidence per level id. */
  rubric: { id: string; label: string; points: string; description?: string; evidence?: Record<string, string> }[];
  /** Shared levels (S3, migration 214); absent or empty keeps the flat rubric contract. */
  levels?: LevelDraft[];
  /** Levels set aside while marking by levels is off: never sent, and restored exactly when it is on again. */
  levelsAside?: LevelDraft[];
  scale?: GradingScale | null;
}

/** The starting levels a teacher adjusts: four, the top at 100. */
export const defaultLevels = (): LevelDraft[] => [["Beginning", "0"], ["Developing", "50"], ["Secure", "80"], ["Strong", "100"]]
  .map(([name, share]) => ({ id: crypto.randomUUID(), name, share }));

/** The rubric the API stores: flat criteria exactly as before, or criteria with the shared levels.
 *  `value` keeps a typed number that does not read yet, for the teaching draft only. */
export function rubricBody(assessment: AssessmentDraft, value: (typed: string) => number | string = Number) {
  const levels = assessment.levels?.length ? assessment.levels : null;
  return assessment.rubric.map(criterion => levels ? {
    id: criterion.id, label: criterion.label.trim(), points: value(criterion.points), description: (criterion.description ?? "").trim(),
    levels: levels.map(level => ({ id: level.id, name: level.name.trim(), share: value(level.share),
      ...(criterion.evidence?.[level.id]?.trim() ? { evidence: criterion.evidence[level.id].trim() } : {}) })),
  } : { id: criterion.id, label: criterion.label.trim(), points: value(criterion.points) });
}

/** What an assessment sends. Levels set aside, and the writing kept for them, stay in the draft. */
const sentAssessment = (assessment: AssessmentDraft) =>
  JSON.stringify([assessment.kind, assessment.externalUrl, rubricBody(assessment, typed => typed.trim()), assessment.scale ?? null]);

/** A scored rubric decides the work's points; the typed score-only total waits aside for a return to scoring without one. */
export const rubricActive = (assessment: AssessmentDraft) => assessment.kind === "scored" && !assessment.scale && assessment.rubric.length > 0;

/** A rubric's total: its criteria's points summed in hundredths, or null while one does not read. */
export function rubricTotal(rubric: AssessmentDraft["rubric"]): number | null {
  if (!rubric.length || rubric.some(criterion => !criterion.points.trim() || !readPoints(criterion.points).ok)) return null;
  return rubric.reduce((sum, criterion) => sum + Math.round(Number(criterion.points) * 100), 0) / 100;
}

/** The points a work carries: its active rubric's total, else what was typed. */
export function workPoints(draft: Pick<WorkDraft, "points" | "assessment">): Reading<number | null> {
  return rubricActive(draft.assessment) ? { ok: true, value: rubricTotal(draft.assessment.rubric) } : readPoints(draft.points);
}

/** What is wrong at each field of an active level rubric, by level or criterion id. An empty
 *  field's message is its own to show once the field is left; the rest are said at once. */
export interface LevelFieldProblems { name: Record<string, string>; share: Record<string, string>; points: Record<string, string> }

export function levelFieldProblems(assessment: AssessmentDraft): LevelFieldProblems {
  const problems: LevelFieldProblems = { name: {}, share: {}, points: {} };
  const levels = assessment.levels ?? [];
  if (!levels.length) return problems;
  const key = (level: LevelDraft) => level.name.trim().toLowerCase();
  const shares = levels.map(level => /^\d+(\.\d+)?$/.test(level.share.trim()) ? Number(level.share) : NaN);
  const valid = shares.filter(share => share >= 0 && share <= 100);
  levels.forEach((level, at) => {
    if (!level.name.trim()) problems.name[level.id] = a3Copy.nameThisLevel;
    else if (levels.some((other, index) => index !== at && key(other) === key(level))) problems.name[level.id] = a3Copy.anotherLevelHasThisName;
    const share = shares[at];
    if (!(share >= 0 && share <= 100)) problems.share[level.id] = a3Copy.giveAShareFrom0To100;
    else if (shares.some((other, index) => index !== at && other === share)) problems.share[level.id] = a3Copy.anotherLevelEarnsThisShare;
  });
  if (valid.length && !valid.includes(100)) {
    const top = levels[shares.indexOf(Math.max(...valid))];
    problems.share[top.id] ??= a3Copy.theTopLevelEarns100;
  }
  for (const criterion of assessment.rubric) {
    const typed = criterion.points.trim();
    if (typed && (!/^\d+(\.\d{1,2})?$/.test(typed) || Number(typed) > 1000)) problems.points[criterion.id] = a3Copy.levelPointsToTheHundredth(criterion.label.trim() || a3Copy.criterion2.trim());
  }
  return problems;
}

/** What keeps a level rubric from publishing, or null. */
export function levelsProblem(assessment: AssessmentDraft): string | null {
  const levels = assessment.levels ?? [];
  if (!levels.length) return null;
  if (levels.length < 2 || levels.length > 10) return a3Copy.levelsAreTwoToTen;
  if (levels.some(level => !level.name.trim())) return a3Copy.nameEveryLevel;
  if (new Set(levels.map(level => level.name.trim().toLowerCase())).size !== levels.length) return a3Copy.eachLevelNeedsItsOwnName;
  const shares = levels.map(level => level.share.trim() === "" ? NaN : Number(level.share));
  if (shares.some(share => !Number.isFinite(share) || share < 0 || share > 100) || new Set(shares).size !== shares.length || Math.max(...shares) !== 100) return a3Copy.levelSharesAreDistinct;
  const vague = assessment.rubric.find(criterion => !(criterion.description ?? "").trim());
  if (vague) return a3Copy.sayWhatToLookFor(vague.label.trim() || a3Copy.criterion2.trim());
  const inexact = assessment.rubric.find(criterion => !/^\d+(\.\d{1,2})?$/.test(criterion.points.trim()) || Number(criterion.points) > 1000);
  if (inexact) return a3Copy.levelPointsToTheHundredth(inexact.label.trim() || a3Copy.criterion2.trim());
  return null;
}

export const ASSESSMENT_KIND_LABEL: Record<AssessmentKind, string> = {
  completion: a3Copy.completion,
  scored: a3Copy.scored,
  external_quiz: a3Copy.quizCompletedElsewhere,
  in_class: a3Copy.inClass,
  native_quiz: "Native quiz",
};

/** A work's facts as its fields hold them. */
export interface WorkDraft {
  policy?: PolicySelection | null;
  audience?: AudienceSelection | null;
  opens?: string;
  title: string;
  instructions: string;
  /** "" is the legacy "Any submission": kept while untouched, never chosen. */
  deliverable: ExpectedDeliverable | "";
  points: string;
  /** A `datetime-local` value in the course zone; "" is no due date. */
  due: string;
  /** "" is the whole course. */
  unitId: string;
  /** "" is not graded. */
  gradeItemId: string;
  /** These terms are saved together: a kind never leaves an incompatible link or rubric. */
  assessment: AssessmentDraft;
  /** Attached existing course materials, in brief order; absent where the record did not say. */
  materialIds?: string[];
}

export type WorkFact = "title" | "instructions" | "assessment" | "deliverable" | "points" | "due" | "class" | "gradeItem";

/** The facts in the editor's order. */
export const WORK_FACTS: readonly WorkFact[] = ["title", "instructions", "assessment", "deliverable", "points", "due", "class", "gradeItem"];

export const KEY_OF: Record<WorkFact, keyof WorkDraft> = {
  title: "title",
  instructions: "instructions",
  assessment: "assessment",
  deliverable: "deliverable",
  points: "points",
  due: "due",
  class: "unitId",
  gradeItem: "gradeItemId",
};

export const FACT_LABEL: Record<WorkFact, string> = {
  title: a3Copy.title,
  instructions: a3Copy.instructions,
  assessment: a3Copy.kindQuizLinkAndRubric,
  deliverable: a3Copy.whatLearnersHandIn,
  points: a3Copy.points,
  due: a3Copy.due5,
  class: a3Copy.session,
  gradeItem: a3Copy.gradeItem,
};

/** What a new draft asks learners to hand in until the teacher chooses. */
export const DEFAULT_DELIVERABLE: SubmissionKind = "text";

export const ANY_SUBMISSION = a3Copy.anySubmission;
export const NOT_GRADED = a3Copy.notGraded;
export const WHOLE_COURSE = a3Copy.wholeCourse;
export const GRADED_WITHOUT_POINTS = a3Copy.gradedWorkNeedsPointsAboveZero;
export const REASON_REQUIRED = a3Copy.sayWhyTheTermsChanged;

export function deliverableLabel(kind: ExpectedDeliverable | "" | null): string {
  if (kind === "none") return a3Copy.nothingToHandIn;
  return kind ? EXPECTED_DELIVERABLE_LABEL[kind] : ANY_SUBMISSION;
}

/** The stored instant as the course's wall clock, for a `datetime-local` field. */
export function dueInputValue(instant: string | null, timeZone: string): string {
  if (!instant) return "";
  const date = new Date(instant);
  if (Number.isNaN(date.getTime())) return "";
  try {
    const parts = Object.fromEntries(
      new Intl.DateTimeFormat("en-CA", {
        timeZone,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        hourCycle: "h23",
      }).formatToParts(date).map((part) => [part.type, part.value]),
    );
    return `${parts.year}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}`;
  } catch {
    return "";
  }
}

type Reading<T> = { ok: true; value: T } | { ok: false; message: string };

/** A typed course-local minute as the instant it names, or why it names none. */
export function readDue(value: string, timeZone: string): Reading<string | null> {
  const deadline = classroomDeadline(value, timeZone);
  if (deadline.kind === "empty") return { ok: true, value: null };
  if (deadline.kind !== "valid") return { ok: false, message: deadline.message };
  const date = new Date(0);
  date.setUTCFullYear(deadline.due_date.year, deadline.due_date.month - 1, deadline.due_date.day);
  date.setUTCHours(deadline.due_time.hours, deadline.due_time.minutes, 0, 0);
  return { ok: true, value: date.toISOString() };
}

const POINTS = /^\d{1,4}(?:\.\d{1,2})?$/;

export function readPoints(value: string): Reading<number | null> {
  const text = value.trim();
  if (!text) return { ok: true, value: null };
  return POINTS.test(text) ? { ok: true, value: Number(text) } : { ok: false, message: a3Copy.useANumberFrom0To999999 };
}

/**
 * Points as a number. The database keeps them as a two-place decimal, which a
 * write's response carries as text ("10.00"); a read of the session carries a
 * number. Both mean the same points.
 */
export function pointsOf(value: number | string | null | undefined): number | null {
  return value == null ? null : Number(value);
}

export function titleProblem(title: string): string | null {
  return title.trim() ? null : a3Copy.addATitle;
}

export function draftOf(work: WorkRecord, timeZone: string): WorkDraft {
  return {
    policy: work.policy,
    audience: work.audience,
    opens: dueInputValue(work.opens_at ?? null, timeZone),
    title: work.title,
    instructions: work.instructions ?? "",
    deliverable: work.expected_deliverable ?? "",
    points: work.points == null ? "" : String(pointsOf(work.points)),
    due: dueInputValue(work.due_at, timeZone),
    unitId: work.unit_id ?? "",
    gradeItemId: work.grade_item_id ?? "",
    assessment: {
      kind: work.assessment_kind ?? (work.points == null ? "completion" : "scored"),
      externalUrl: work.external_url ?? "",
      rubric: (work.rubric ?? []).map(({ levels, ...criterion }) => ({ ...criterion, points: String(Number(criterion.points)),
        ...(levels?.length ? { description: criterion.description ?? "", evidence: Object.fromEntries(levels.flatMap(level => level.evidence ? [[level.id, level.evidence]] : [])) } : {}) })),
      ...(work.rubric?.[0]?.levels?.length ? { levels: work.rubric[0].levels.map(level => ({ id: level.id, name: level.name, share: String(level.share) })) } : {}),
      ...(work.scale ? { scale: work.scale } : {}),
    },
    materialIds: work.materials?.map(material => String(material.material_id)),
  };
}

function differs(fact: WorkFact, draft: WorkDraft, saved: WorkDraft, base: WorkRecord): boolean {
  switch (fact) {
    case "title":
      return draft.title.trim() !== saved.title;
    case "instructions":
      return draft.instructions.trim() !== saved.instructions;
    case "assessment":
      return sentAssessment(draft.assessment) !== sentAssessment(saved.assessment);
    case "points": {
      const points = workPoints(draft);
      return !points.ok || points.value !== pointsOf(base.points);
    }
    default:
      return draft[KEY_OF[fact]] !== saved[KEY_OF[fact]];
  }
}

/** The facts typed or chosen here and not yet saved, sendable or not. */
export function typedFacts(draft: WorkDraft, base: WorkRecord, timeZone: string): WorkFact[] {
  const saved = draftOf(base, timeZone);
  return WORK_FACTS.filter((fact) => differs(fact, draft, saved, base));
}

/**
 * Why each changed fact cannot be sent as it stands. Only a changed fact is
 * judged, so terms nobody touched are never refused. Graded work needs points
 * above zero: the message sits under the grade item while it can be chosen,
 * and under Points once it cannot.
 */
export function problemsOf(
  draft: WorkDraft,
  base: WorkRecord,
  timeZone: string,
  status: AssignmentStatus,
): Partial<Record<WorkFact, string>> {
  const changed = new Set(typedFacts(draft, base, timeZone));
  const problems: Partial<Record<WorkFact, string>> = {};
  if (changed.has("title")) {
    const problem = titleProblem(draft.title);
    if (problem) problems.title = problem;
  }
  const points = workPoints(draft);
  if (changed.has("points") && !points.ok) problems.points = points.message;
  if (changed.has("due")) {
    const due = readDue(draft.due, timeZone);
    if (!due.ok) problems.due = due.message;
  }
  if (changed.has("assessment") || changed.has("points") && draft.assessment.rubric.length) {
    const { kind, externalUrl, rubric, scale } = draft.assessment;
    let problem: string | null = null;
    if (kind === "external_quiz") {
      try {
        const url = new URL(externalUrl.trim());
        if (url.protocol !== "https:" || url.username || url.password) problem = a3Copy.useAnHTTPSQuizLinkWithoutCredentials;
      } catch { problem = a3Copy.addTheHTTPSQuizLink; }
    }
    if (kind === "scored" && rubric.length) {
      if (rubric.length > 20 || rubric.some(criterion => !criterion.label.trim() || !readPoints(criterion.points).ok || !(Number(criterion.points) > 0))) {
        problem = a3Copy.eachRubricCriterionNeedsALabelAndPointsAboveZero;
      } else {
        problem = levelsProblem(draft.assessment);
      }
    }
    if (kind === "scored" && scale && status !== "draft") {
      try { validateScale({ scale, points: scaleMaximum(scale) }); }
      catch (error) { problem = (error as Error).message; }
      if (rubric.length) problem = "Use one grading scale for this assignment.";
    }
    if (problem) {
      problems.assessment = problem;
      // A rubric and its points must change in one valid set of terms.
      if (changed.has("points") && kind === "scored" && rubric.length) problems.points ??= problem;
    }
  }
  const gradeItemId = status === "draft" ? draft.gradeItemId : base.grade_item_id ?? "";
  // Preserve legacy validator callers; explicitly chosen completion and
  // in-class kinds can feed a line without numeric points (ADR 0032).
  const needsPoints = base.assessment_kind === undefined && !changed.has("assessment")
    || draft.assessment.kind === "scored" && !draft.assessment.scale || draft.assessment.kind === "external_quiz";
  const graded = needsPoints && gradeItemId !== "" && points.ok && !(points.value !== null && points.value > 0);
  if (graded && (changed.has("points") || changed.has("gradeItem") || changed.has("assessment"))) {
    const at: WorkFact = status === "draft" && changed.has("gradeItem") ? "gradeItem" : "points";
    problems[at] = GRADED_WITHOUT_POINTS;
    // Neither half of the pair is sent while it stands.
    if (at === "gradeItem" && changed.has("points")) problems.points ??= "";
    if (at === "points" && changed.has("gradeItem")) problems.gradeItem ??= "";
    if (changed.has("assessment")) problems.assessment ??= GRADED_WITHOUT_POINTS;
  }
  return problems;
}

/** The terms half of a write: what the fields send, without a reason or token. */
export type WorkChanges = Omit<CorrectAssignmentRequest, "reason" | "expected_updated_at">;

/**
 * What the fields hold that the work does not, as one write sends it. A fact
 * with a problem stays in its field. The grade item is sent only on a draft.
 */
export function changesFrom(
  draft: WorkDraft,
  base: WorkRecord,
  timeZone: string,
  status: AssignmentStatus,
): { body: WorkChanges; facts: Set<WorkFact> } {
  const problems = problemsOf(draft, base, timeZone, status);
  const body: WorkChanges = {};
  const facts = new Set<WorkFact>();
  for (const fact of typedFacts(draft, base, timeZone)) {
    if (fact in problems) continue;
    if (fact === "gradeItem" && status !== "draft") continue;
    facts.add(fact);
    switch (fact) {
      case "title":
        body.title = draft.title.trim();
        break;
      case "instructions":
        body.instructions = draft.instructions.trim() || null;
        break;
      case "assessment": {
        const { kind, externalUrl, rubric, scale } = draft.assessment;
        body.assessment_kind = kind;
        body.external_url = kind === "external_quiz" ? externalUrl.trim() : null;
        body.rubric = kind === "scored" && rubric.length ? rubricBody(draft.assessment) as RubricCriterion[] : null;
        if (scale || base.scale) body.scale = kind === "scored" && scale ? { rows: scale.rows.map(row => ({ ...row, score: status === "draft" ? row.score : Number(row.score), level: row.level, meaning: row.meaning })) } : null;
        if (kind === "scored" && scale) body.points = scaleMaximum(scale);
        if (rubricActive(draft.assessment)) body.points = rubricTotal(rubric);
        body.expected_deliverable = kind === "external_quiz" || kind === "in_class" ? "none"
          : kind === "scored" && draft.deliverable === "none" ? DEFAULT_DELIVERABLE : draft.deliverable || DEFAULT_DELIVERABLE;
        break;
      }
      case "deliverable":
        if (draft.deliverable) body.expected_deliverable = draft.assessment.kind === "external_quiz" || draft.assessment.kind === "in_class" ? "none"
          : draft.assessment.kind === "scored" && draft.deliverable === "none" ? DEFAULT_DELIVERABLE : draft.deliverable;
        break;
      case "points": {
        const points = workPoints(draft);
        if (points.ok) body.points = points.value;
        break;
      }
      case "due": {
        const due = readDue(draft.due, timeZone);
        if (due.ok) body.due_at = due.value;
        break;
      }
      case "class":
        body.unit_id = draft.unitId || null;
        break;
      case "gradeItem":
        body.grade_item_id = draft.gradeItemId || null;
        break;
    }
  }
  return { body, facts };
}

/** A due instant as the row and the notice read it, in the course zone. */
export function dueText(instant: string | null, timeZone: string): string {
  if (!instant) return a3Copy.noDueDate;
  return formatInCourseZone(instant, timeZone, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    timeZoneName: "short",
  });
}

export interface WorkLabels {
  classLabel: (unitId: string) => string;
  gradeItemLabel: (gradeItemId: string) => string;
  /** An attached course material's title, where the surface has read the course's materials. */
  materialLabel?: (materialId: string) => string;
}

/** Their facts that differ from what was typed, in the editor's order. */
export function workConflictFacts(
  mine: WorkDraft,
  theirs: WorkRecord,
  timeZone: string,
  labels: WorkLabels,
): ConflictFact[] {
  const other = draftOf(theirs, timeZone);
  const shown = (draft: WorkDraft, fact: WorkFact): string => {
    switch (fact) {
      case "title":
        return draft.title.trim();
      case "instructions":
        return draft.instructions.trim();
      case "assessment":
        return [ASSESSMENT_KIND_LABEL[draft.assessment.kind], draft.assessment.externalUrl,
          ...draft.assessment.rubric.map(criterion => `${criterion.label}: ${criterion.points} points`)].filter(Boolean).join(" · ");
      case "deliverable":
        return deliverableLabel(draft.deliverable);
      case "points":
        return draft.points.trim() || a3Copy.noPoints;
      case "due":
        return draft.due;
      case "class":
        return labels.classLabel(draft.unitId);
      case "gradeItem":
        return labels.gradeItemLabel(draft.gradeItemId);
    }
  };
  return WORK_FACTS
    .filter((fact) => shown(mine, fact) !== shown(other, fact))
    .map((fact) => ({
      label: FACT_LABEL[fact],
      theirs: fact === "due" ? dueText(theirs.due_at, timeZone) : shown(other, fact),
    }));
}
